import { AddIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Show,
  useMediaQuery,
  useToast,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import axios from "axios";
import * as React from "react";
import { useHistory } from "react-router-dom";
import { Announcement } from "types/apiTypes";
import createPersistedState from "use-persisted-state";
import { getRole } from "utils/functions";
import useStore from "utils/store";
import { api } from "../../config";
import AnnouncementModal from "./AnnouncementModal";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const ViewAnnouncements: React.FC = () => {
  const useCounterState = createPersistedState("sortQueryAnnouncement");
  const [sortQueryAnnouncement, setSortQueryAnnouncement]: any =
    useCounterState("id:asc");
  const history = useHistory();
  const toast = useToast();
  const [openModal, setOpenModal] = React.useState(false);
  const [announcement, setAnnouncement] = React.useState<Announcement>();
  const isClient = getRole() === "Client";
  const [isLargerThanMd] = useMediaQuery("(min-width: 48em)");

  const setAnnouncementsCount = useStore(
    (state) => state.setAnnouncementsCount
  );

  const fetchAnnouncementsAndCount = async () => {
    try {
      const response = await axios.get(
        isClient
          ? `${api}/announcements/find-distinct?_limit=50&_start=0`
          : `${api}/announcements/find-my?_limit=50&_start=0&_sort=dated:DESC`,
        { withCredentials: true }
      );
      const announcements = response.data.data;
      const totalCount = response.data.count;
      if (!isClient) {
        const count = announcements.filter(
          (announcement: any) => announcement.read === false
        ).length;
        setAnnouncementsCount(count);
      }

      return { announcements, totalCount };
    } catch (error: any) {
      toast({
        title: error.message,
        position: "bottom-right",
        status: "error",
      });
      return { announcements: [], totalCount: 0 };
    }
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["announcements"],
    queryFn: fetchAnnouncementsAndCount,
  });

  const handleAddClick = () => {
    history.push("/admin/announcement-form");
  };

  const handleDeleteClick = async (id: string) => {
    try {
      await axios.delete(`${api}/announcements/${id}`, {
        withCredentials: true,
      });
      toast({
        title: "Announcement deleted successfully",
        position: "bottom-right",
        status: "success",
      });
      refetch();
    } catch (error: any) {
      toast({
        title: error.message,
        position: "bottom-right",
        status: "error",
      });
    }
  };

  const handleRowClick = async (rowData: any) => {
    if (!isClient) {
      setAnnouncement(rowData);
      setOpenModal(true);
      await axios.put(
        `${api}/announcements/${rowData.id}`,
        { read: true },
        { withCredentials: true }
      );
      refetch();
    }
  };

  return (
    <>
      {isClient && (
        <Button
          colorScheme="blue"
          onClick={handleAddClick}
          className="add-btn"
          m="3"
          size="sm"
          leftIcon={<AddIcon />}
          textTransform="uppercase">
          Add Announcement
        </Button>
      )}
      <Flex flexDirection="row" px="2">
        <Box style={{ flexGrow: 4 }}>
          {isLoading ? (
            <Spinner />
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Title</Th>
                  <Th>Message</Th>
                  {isClient && <Th>Actions</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {data?.announcements.map((item: any) => (
                  <Tr
                    key={item.id}
                    onClick={() => handleRowClick(item)}
                    style={{
                      backgroundColor: isClient
                        ? "white"
                        : item.read
                        ? "white"
                        : "#f6ad55",
                      cursor: !isClient ? "pointer" : "default",
                    }}>
                    <Td>{item.dated.substring(0, 16).replace("T", " ")}</Td>
                    <Td>{item.title}</Td>
                    <Td>{item.message}</Td>
                    {isClient && (
                      <Td>
                        <Button
                          colorScheme="red"
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm("Are you sure?")) {
                              handleDeleteClick(item.messageCode);
                            }
                          }}>
                          Delete
                        </Button>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
        {!isClient && isLargerThanMd && (
          <Show above="sm">
            <Box>
              <img src="../../undraw_happy_news_re_tsbd.svg" alt="no" />
            </Box>
          </Show>
        )}
      </Flex>

      <AnnouncementModal
        openModal={openModal}
        announcement={announcement}
        setOpenModal={setOpenModal}
      />
    </>
  );
};

export default ViewAnnouncements;
